import { BundleModel } from "../Models/BundleModel";
import { BundleThemeModel } from "../Models/BundleThemeModel";
import { IComponent } from "../Models/IComponent";
import { IContainerComponent } from "../Models/IContainerComponent";
import { ReactBundleModel } from "../Models/ReactBundleModel";

/**
 * postMessage message type constants
 * These constants ensure type safety and consistency between app-portal and app-runtime
 *
 * NOTE: This file must be kept in sync with app-portal's DesignerMessages.ts
 */

/**
 * Special component type used to wrap SharedComponents for rendering in the designer preview.
 * app-portal wraps the component in this container, app-runtime unwraps it.
 */
export const SHARED_COMPONENT_ROOT_TYPE = "SharedComponentRoot";
export const DESIGNER_PROTOCOL_VERSION = 1;

export const MESSAGE_TYPES = {
  IFRAME_READY: "IFRAME_READY",
  AUTH_REQUIRED: "AUTH_REQUIRED",
  AUTH_STATE_CHANGED: "AUTH_STATE_CHANGED",
  SET_BUNDLE: "SET_BUNDLE",
  BUNDLE_COMPONENT_UPDATED: "BUNDLE_COMPONENT_UPDATED",
  BUNDLE_METADATA_UPDATED: "BUNDLE_METADATA_UPDATED",
  REACT_BUILD_UPDATED: "REACT_BUILD_UPDATED",
  RENDER_VIEW: "RENDER_VIEW",
  UPDATE_SELECTION: "UPDATE_SELECTION",
  COMPONENT_SELECTED: "COMPONENT_SELECTED",
  COMPONENT_UPDATED: "COMPONENT_UPDATED",
  ADD_COMPONENT: "ADD_COMPONENT",
  DELETE_COMPONENT: "DELETE_COMPONENT",
  NAVIGATE_TO_VIEW: "NAVIGATE_TO_VIEW",
  NAVIGATE_TO_PATH: "NAVIGATE_TO_PATH",
  NAVIGATE_BACK: "NAVIGATE_BACK",
  VIEW_ACTIVATED: "VIEW_ACTIVATED",
  SET_VIEW_ROUTES: "SET_VIEW_ROUTES",
} as const;

/**
 * postMessage communication protocol between app-runtime and app-portal
 * for the Live Preview feature (Option 1B)
 */
export type DesignerMessage =
  | { type: typeof MESSAGE_TYPES.IFRAME_READY; payload: { protocolVersion: number } }
  | { type: typeof MESSAGE_TYPES.AUTH_REQUIRED; payload: { reason: "token-unavailable" | "unauthorized" } }
  | { type: typeof MESSAGE_TYPES.AUTH_STATE_CHANGED; payload: { isAuthenticated: boolean } }
  | {
      type: typeof MESSAGE_TYPES.SET_BUNDLE;
      payload: { bundle: BundleModel };
    }
  | {
      type: typeof MESSAGE_TYPES.BUNDLE_COMPONENT_UPDATED;
      payload: {
        scope: "View" | "SharedComponent";
        ownerId: string;
        componentPath: string[];
        component: IComponent;
      };
    }
  | {
      type: typeof MESSAGE_TYPES.BUNDLE_METADATA_UPDATED;
      payload: {
        theme?: Record<string, any>;
        themes?: BundleThemeModel[];
        settings?: BundleModel["Settings"];
        embeddedResources?: BundleModel["EmbeddedResources"];
        enums?: BundleModel["Enums"];
      };
    }
  | {
      type: typeof MESSAGE_TYPES.REACT_BUILD_UPDATED;
      payload: {
        buildId: string;
        reactBundle: ReactBundleModel;
      };
    }
  | {
      type: typeof MESSAGE_TYPES.RENDER_VIEW;
      payload: {
        view: IContainerComponent;
        editMode: boolean;
        selectedPath: string[] | null;
      };
    }
  | {
      type: typeof MESSAGE_TYPES.UPDATE_SELECTION;
      payload: { path: string[] };
    }
  | {
      type: typeof MESSAGE_TYPES.COMPONENT_SELECTED;
      payload: { id: string; path: string[]; viewId?: string; sharedComponentId?: string; referenceComponentPath?: string[] };
    }
  | {
      type: typeof MESSAGE_TYPES.COMPONENT_UPDATED;
      payload: { id: string; path: string[]; updates: Partial<IComponent> };
    }
  | {
      type: typeof MESSAGE_TYPES.ADD_COMPONENT;
      payload: { parentPath: string[] };
    }
  | {
      type: typeof MESSAGE_TYPES.DELETE_COMPONENT;
      payload: { path: string[] };
    }
  | {
      type: typeof MESSAGE_TYPES.NAVIGATE_TO_VIEW;
      payload: { viewId: string; routePath: string };
    }
  | {
      type: typeof MESSAGE_TYPES.NAVIGATE_TO_PATH;
      payload: { path: string };
    }
  | { type: typeof MESSAGE_TYPES.NAVIGATE_BACK }
  | {
      type: typeof MESSAGE_TYPES.VIEW_ACTIVATED;
      payload: { viewId: string; currentPath: string };
    }
  | {
      type: typeof MESSAGE_TYPES.SET_VIEW_ROUTES;
      payload: { viewRoutes: Array<{ viewId: string; routePath: string | null }> };
    };
